/**
 * The `useApi` function is a custom hook in a React application that handles API requests with
 * authentication using AWS Cognito and Axios.
 * @returns The `useApi` function returns an object containing four functions: `getAPI`, `PostAPI`,
 * `PatchAPI`, and `DeleteAPI`. These functions are used to make API requests with proper authorization
 * headers using the Cognito access token.
 */

import { fetchAuthSession, signOut } from 'aws-amplify/auth'
import axios from 'axios';
import config from '../config/config';

// List of public API endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
    'admins/forgot-password',
    'admins/reset-password',
    'admins/verify-token',
];

/**
 * Checks if an endpoint is public (doesn't require authentication)
 */
const isPublicEndpoint = (endpoint: string): boolean => {
    return PUBLIC_ENDPOINTS.some(publicEndpoint => endpoint.includes(publicEndpoint));
};

const useApi = () => {
    /**
     * Fetches the access token for the current authenticated user using AWS Amplify's `Auth` module.
     * @returns The access token string.
     * @throws If the user is not authenticated or the token cannot be retrieved.
     */
    const baseURL = config.baseURL;

    /**
     * Logs out the user silently
     */
    const silentLogout = () => {
        try {
            signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
        localStorage.clear();
        window.location.replace('/');
    };

    /**
     * Checks if token is undefined and logs out (only for private endpoints)
     * @param authToken - The authentication token
     * @param endpoint - The API endpoint being called
     * @returns true if logout was triggered, false otherwise
     */
    const logoutIfNoToken = (authToken: string | undefined, endpoint: string): boolean => {
        // Skip token check for public endpoints
        if (isPublicEndpoint(endpoint)) {
            return false;
        }
        
        // For private endpoints, redirect to login if token is missing
        if (!authToken) {
            silentLogout();
            return true;
        }
        return false;
    };

    /**
     * Makes a GET request to the specified endpoint with authorization headers.
     * @returns An object with `error` and `data` properties.
     */
    const getAPI = async (endpoint: string, header = true, headers?: any) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            if (logoutIfNoToken(authToken, endpoint)) return { error: false, data: null };
            
            let defaultHeaders: Record<string, string> = {};
            // Only add Authorization header if header is true and token exists
            if (header && authToken) {
                defaultHeaders = { Authorization: `Bearer ${authToken}` };
            }
            const finalHeaders = headers ? { ...defaultHeaders, ...headers } : defaultHeaders;
            let data;
            if (header && Object.keys(finalHeaders).length > 0) {
                data = await axios.get(`${baseURL}${endpoint}`, { headers: finalHeaders });
            } else {
                data = await axios.get(`${baseURL}${endpoint}`);
            }
            return { error: false, data: data.data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle 411 - silent logout (only for private endpoints)
                if (error.response?.status === 411 && !isPublicEndpoint(endpoint)) {
                    silentLogout();
                    return { error: false, data: null };
                }
                // Handle Axios-specific error
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                return { error: true, data: error.response };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };

    /**
     * Makes a POST request to the specified endpoint with authorization headers and a payload.
     * @returns An object with `error` and `data` properties.
     */
    const PostAPI = async (endpoint: any, payload: any, header = true) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            if (logoutIfNoToken(authToken, endpoint)) return { error: false, data: null };
            
            let headers: any = {};
            // Only add Authorization header if header is true and token exists
            if (header && authToken) {
                headers = { Authorization: `Bearer ${authToken}` };
            }
            
            const { data } = await axios.post(`${baseURL}${endpoint}`, payload, { headers });
            return { error: false, data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle 411 - silent logout (only for private endpoints)
                if (error.response?.status === 411 && !isPublicEndpoint(endpoint)) {
                    silentLogout();
                    return { error: false, data: null };
                }
                // Handle Axios-specific error
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                return { error: true, data: error };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };

    /**
     * Makes a PATCH request to the specified endpoint with authorization headers and a payload.
     * @returns An object with `error` and `data` properties.
     */
    const PatchAPI = async (endpoint: any, payload?: any, header = true, headers?: any, useXAccessToken = false) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            if (logoutIfNoToken(authToken, endpoint)) return { error: false, data: null };
            const accessToken = (await fetchAuthSession()).tokens?.accessToken?.toString();
            let defaultHeaders: any = {};
            // Only add Authorization header if header is true and token exists
            if (header && authToken) {
                if (useXAccessToken && accessToken) {
                    defaultHeaders = { 'x-access-token': accessToken, Authorization: `Bearer ${authToken}` };
            } else {
                defaultHeaders = { Authorization: `Bearer ${authToken}` };
                }
            }
            const finalHeaders = headers ? { ...defaultHeaders, ...headers } : defaultHeaders;
            let data;
            if (header && Object.keys(finalHeaders).length > 0) {
                data = await axios.patch(`${baseURL}${endpoint}`, payload, { headers: finalHeaders });
            } else {
                data = await axios.patch(`${baseURL}${endpoint}`, payload);
            }
            return { error: false, data: data.data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle 411 - silent logout (only for private endpoints)
                if (error.response?.status === 411 && !isPublicEndpoint(endpoint)) {
                    silentLogout();
                    return { error: false, data: null };
                }
                // Handle Axios-specific error
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                return { error: true, data: error };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };
    /**
     * Makes a PUT request to the specified endpoint with authorization headers and a payload.
     * @returns An object with `error` and `data` properties.
     */
    const PutAPI = async (endpoint: any, payload?: any, header = true) => {
        try {
            await fetchAuthSession()
            const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
            if (logoutIfNoToken(authToken, endpoint)) return { error: false, data: null };
            let headers: any = {};
            // Only add Authorization header if header is true and token exists
            if (header && authToken) {
                headers = { Authorization: `Bearer ${authToken}` };
            }
            let data;
            if (header && Object.keys(headers).length > 0) {
                data = await axios.put(`${baseURL}${endpoint}`, payload, { headers });
            } else {
                data = await axios.put(`${baseURL}${endpoint}`, payload);
            }
            return { error: false, data: data.data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // Handle 411 - silent logout (only for private endpoints)
                if (error.response?.status === 411 && !isPublicEndpoint(endpoint)) {
                    silentLogout();
                    return { error: false, data: null };
                }
                // Handle Axios-specific error
                if (((error.message.includes('User needs to be authenticated')) || (error.name === 'UserUnAuthenticatedException') || (error.message === 'Network Error') ||
                (error.message.includes('Access Token has been revoked')) || (error.name === 'NotAuthorizedException'))) {
                    // console.log(error);
                    // window.location.reload();
                    // localStorage.clear();
                }
                return { error: true, data: error };
            } else {
                // Handle non-Axios errors
                return { error: true, data: 'An unexpected error occurred' };
            }
        }
    };    return { getAPI, PostAPI, PatchAPI, PutAPI };
};

export default useApi;